import { MessageSquareText, MessagesSquare } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { ConversationTexts } from "@/features/conversations/thread";
import { recordConversation, textConversation } from "@/lib/conversations-from-api";
import { textsMissing, useContact, useReadConversation, useTextConversations } from "@/lib/queries";
import type { TextConversation } from "@/lib/types";
import { cn } from "@/lib/utils";
import { seesCampaigns } from "@/lib/types";
import { useAuth } from "@/lib/auth";

/**
 * The Phone page's Texts pane (ACL-561): the lead's texts beside the call, the pictures they send
 * among them, and a box to answer in. A lead on the phone with a rep often texts what the rep has
 * just asked for — a licence, an insurance card, the damaged car — and before this the rep had to
 * leave the call's page to see it.
 *
 * It is the Conversations screen's own thread cut down to the texts (`ConversationTexts`), not a
 * second one: the same pictures in the same states, the same composer with the same rules about
 * what may leave. "Open in Conversations" has the rest of the thread.
 */

/**
 * The lead's conversation, found as the Conversations page finds a lead's thread (`?lead=`): the
 * newest of theirs (`GET /conversations?lead_id=`). Undefined until the answer for THIS lead is in
 * — the list keeps its last answer on screen while it asks again, which here would be the previous
 * lead's texts beside this lead's call — and null when they have none. A contact the team has only
 * ever called is listed too, as a row of its own (`channel: "call"`) that holds no texts, so it
 * does not count as one; nor does their email thread (ADR-180), whose emails are no texts.
 */
export function useLeadConversation(leadId: string | null) {
  const list = useTextConversations({ lead_id: leadId ?? undefined }, 0, { enabled: leadId !== null });
  const known = leadId !== null && list.isSuccess && !list.isPlaceholderData;
  const row: TextConversation | null | undefined = known ? leadTextThread(list.data.items) : undefined;
  return { row, list };
}

/** The newest of a lead's conversations that holds their texts, or null. */
export function leadTextThread(items: TextConversation[]): TextConversation | null {
  return items.find((t) => t.channel !== "call" && t.channel !== "email") ?? null;
}

/**
 * Texts the pane shows are read, as opening the thread in Conversations reads them: for everybody
 * who shares its inbox. One that lands while the pane is up is read as it lands, since it is on
 * the rep's screen; that is also what takes the count off the pane's switch.
 */
function useReadOnSight(row: TextConversation | null | undefined): void {
  const { mutate } = useReadConversation();
  const id = row?.id;
  const unread = row?.unread ?? 0;
  React.useEffect(() => {
    if (id !== undefined && unread > 0) mutate({ id });
  }, [id, unread, mutate]);
}

export function TextsPane({
  leadId, className, bodyClassName,
}: {
  leadId: string;
  className?: string;
  /** For what is under the header only, as the script's pane takes it (`ScriptPane`). */
  bodyClassName?: string;
}) {
  const { row, list } = useLeadConversation(leadId);
  // A lead with no texts yet opens as their record, as in Conversations: the composer sends the
  // first text from it. Asked only then.
  const record = useContact(row === null ? leadId : undefined);
  useReadOnSight(row);
  const asRep = !seesCampaigns(useAuth().user?.role);
  const conversation = React.useMemo(
    () => (row ? textConversation(row, asRep) : row === null && record.data ? recordConversation(record.data) : null),
    [row, record.data, asRep],
  );

  let body: React.ReactNode;
  if (list.error && textsMissing(list.error)) {
    body = (
      <EmptyState
        icon={MessageSquareText}
        className="min-h-0 flex-1"
        title="Texts aren't on this server yet"
        description="This server does not keep texts, so there are none to show."
      />
    );
  } else if (row === undefined && list.isError) {
    body = (
      <ErrorState
        error={list.error}
        title="Could not load the texts"
        className="min-h-0 flex-1 p-4"
        action={
          <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
            Try again
          </Button>
        }
      />
    );
  } else if (conversation !== null) {
    body = <ConversationTexts key={conversation.id} conversation={conversation} />;
  } else if (row === undefined || record.isPending) {
    body = (
      <div className="flex min-h-0 flex-1 items-center justify-center" role="status">
        <Spinner className="size-5" />
        <span className="sr-only">Loading the texts</span>
      </div>
    );
  } else {
    // No conversation, and a record the reader may not open: nothing here to read or to send from.
    body = (
      <EmptyState
        icon={MessageSquareText}
        className="min-h-0 flex-1"
        title="No texts to show"
        description="Texts with this contact that you may read appear here."
      />
    );
  }

  return (
    <section aria-label="Texts" className={cn("flex min-h-0 flex-col", className)}>
      {/* The same header the other panes have, so side by side they read as one band. */}
      <header className="shrink-0 border-b border-border px-4 py-2.5">
        <div className="flex min-h-7 items-center gap-2">
          <h2 className="text-sm font-semibold text-text">Texts</h2>
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            <Link to={`/conversations/team?lead=${leadId}`}>
              <MessagesSquare aria-hidden />
              Open in Conversations
            </Link>
          </Button>
        </div>
      </header>
      <div className={cn("relative flex min-h-0 flex-1 flex-col", bodyClassName)}>{body}</div>
    </section>
  );
}
